/* eslint-disable @typescript-eslint/naming-convention */
import { RateSource, ExchangeRate } from '../../../features/exchange-rate/exchange-rate.service';
import { AccountHelper } from '../../../domain/account.domain';
import { Currency } from '../../../domain/market.domain';
import { AutoSuggestSymbol } from '../../../domain/symbol-lookup.domain';
import { SymbolType } from '../../../domain/symbol.domain';
import {
  Confirmation,
  IBMExchangeRates,
  SecuritiesTransfersAccount,
  SecuritiesTransfersHolding,
  TransferType,
  TransferTypeResponse,
  Review
} from './securities-transfers.domain';

export class SecuritiesTransferContext {
  transferToken: string;
  transferFromAccount: SecuritiesTransfersAccount;
  transferToAccount: SecuritiesTransfersAccount;
  contributionType: string;
  cashAmount: number;
  selectedSecurities: Array<SecuritiesTransfersHolding> = [];
  transferClassification?: TransferTypeResponse;
  toAccounts: Array<SecuritiesTransfersAccount> = [];
  cashBalance: number;
  review: Review;
  confirmation: Confirmation;
  cikMessage: string;
  contributionTypeDropdownDisabled: boolean;
  exchangeRates: IBMExchangeRates = {
    IbmCadTOUsd: 0,
    IbmUsdTOCad: 0
  };

  mmtExchangeRate: number = 0;

  onFromAccountChange(): void {
    this.toAccounts = [];
    this.transferToAccount = undefined;
    this.transferClassification = undefined;
    this.cashAmount = undefined;
    this.selectedSecurities = [];
  }

  clear(): void {
    this.transferFromAccount = undefined;
    this.transferToAccount = undefined;
    this.contributionType = undefined;
    this.cashAmount = undefined;
    this.selectedSecurities = [];
    this.toAccounts = [];
    this.transferClassification = undefined;
    this.cashBalance = undefined;
    this.confirmation = undefined;
    this.exchangeRates = {
      IbmCadTOUsd: 0,
      IbmUsdTOCad: 0
    };
  }

  isCikTransferType(): boolean {
    return this.transferClassification !== undefined && this.transferClassification.transferType === TransferType.CIK;
  }

  isExchangeAgreementNotSigned(security: SecuritiesTransfersHolding): boolean {
    return security.exchangeAgreementsSigned !== undefined && security.exchangeAgreementsSigned === false;
  }

  calculateTotalValue(security: SecuritiesTransfersHolding): number {
    const total = security.inputQuantity * security.lastPrice * security.quantityMultiplier;
    return isNaN(total) ? 0 : Math.round(total * 100) / 100;
  }

  /**
   * Return true if selected securities contain a USD security
   */
  hasUsdSecurities(securities: Array<SecuritiesTransfersHolding>): boolean {
    if (securities !== undefined && securities.length > 0) {
      return securities.some((security: SecuritiesTransfersHolding): boolean => this.isUsdSecurity(security));
    }
    return false;
  }

  showUsdWarning(securities: Array<SecuritiesTransfersHolding>): boolean {
    return this.hasUsdSecurities(securities) && AccountHelper.rapCodeInArray(this.transferToAccount, ['J', 'S']);
  }

  hasNumberOfPositionsReachedLimit(): boolean {
    const maxPositionsPerTransfer = this.getMaxPositionsPerTransfer();
    const numberOfCashPositions = this.isCashAmountEntered() ? 1 : 0;
    const numberOfPositions = numberOfCashPositions + this.selectedSecurities.length;
    return numberOfPositions >= maxPositionsPerTransfer;
  }

  showContributionTypes(): boolean {
    return this.transferClassification?.contributionTypeCd?.length > 0;
  }

  getContributionType(): string {
    return this.showContributionTypes() ? this.contributionType : undefined;
  }

  showCashInputField(): boolean {
    return this.transferClassification.transferType === TransferType.JOURNAL
      && this.transferClassification.sameCurrency
      && this.cashBalance !== undefined
      && this.cashBalance > 0;
  }

  showTotalApproximateUsdValue(): boolean {
    return this.transferClassification.transferType === TransferType.JOURNAL
      && !(this.fromAccountCurrency() === Currency.CAD && this.toAccountCurrency() === Currency.CAD);
  }

  fromAccountCurrency(): Currency {
    return this.transferFromAccount !== undefined ? this.transferFromAccount.currency : undefined;
  }

  toAccountCurrency(): Currency {
    return this.transferToAccount !== undefined ? this.transferToAccount.currency : undefined;
  }

  /**
   * Convert cash & security values to the requested currency and calculate the total.
   */
  calculateTotalTransferValue(displayCurrency: Currency): number {
    let total = 0;
    // calculate securities
    if (this.isSecuritiesSelected()) {
      this.selectedSecurities.forEach((security: SecuritiesTransfersHolding): void => {
        const effectiveRate = this.getExchangeRate(security.currency, displayCurrency);
        // According to WMM and IBM logic, the way IBM does it is calculate contribution value and
        // round it before multiplying by any exchange rate. Changing WB to make sure it's displaying accurately to what IBM will do
        const securityTotal = (Math.round(security.inputQuantity * security.lastPrice  * security.quantityMultiplier * 100) / 100)
          * effectiveRate;
        total += !isNaN(securityTotal) ? securityTotal : 0;
      });
    }
    // add cash amount
    if (this.isCashAmountEntered()) {
      const effectiveRate = this.getExchangeRate(this.fromAccountCurrency(), displayCurrency);
      const cashTotal = this.cashAmount * effectiveRate;
      total += !isNaN(cashTotal) ? cashTotal : 0;
    }
    return total;
  }

  getExchangeRate(fromCurrency: Currency, toCurrency: Currency): number {
    if (fromCurrency === Currency.CAD && toCurrency === Currency.USD) {
      return this.exchangeRates.IbmCadTOUsd;
    } else if (fromCurrency === Currency.USD && toCurrency === Currency.CAD) {
      // Use MMT exchange rate if real time transfers and CIK transfer
      if (this.isCikTransferType() && this.mmtExchangeRate !== 0) {
        return this.mmtExchangeRate;
      }
      return this.exchangeRates.IbmUsdTOCad;
    }
    return 1;
  }

  populateExchangeRates(fxRates: Array<ExchangeRate>): void {
    if (Array.isArray(fxRates)) {
      fxRates.forEach((fxRate: ExchangeRate): void => {
        if (fxRate.source === RateSource.IBM && fxRate.from === Currency.CAD && fxRate.to === Currency.USD) {
          this.exchangeRates.IbmCadTOUsd = fxRate.rate;
        } else if (fxRate.source === RateSource.IBM && fxRate.from === Currency.USD && fxRate.to === Currency.CAD) {
          this.exchangeRates.IbmUsdTOCad = fxRate.rate;
        }
      });
    }
  }

  setMmtExchangeRate(security: SecuritiesTransfersHolding): void {
    if (this.isUsdSecurity(security)) {
      this.mmtExchangeRate = security.fxRate;
    }
  }

  isSecuritiesSelected(): boolean {
    return this.selectedSecurities !== undefined && this.selectedSecurities.length > 0;
  }

  /**
   * Some securities have mismatching markets, types and ticker symbols between autosuggest and security details
   * (securitiesTransfers.security) responses - they would pass through the preliminary check ('hasSymbol') and cause
   * the security details to be retrieved from the backend. This is the final duplicates' check: we use retrieved
   * security details IDs to stop duplicates from being added to the resulting 'selected securities' array.
   */
  hasSecurityId(securityId: string): boolean {
    if (this.selectedSecurities !== undefined) {
      return this.selectedSecurities.some(
        (selectedSecurity: SecuritiesTransfersHolding): boolean => selectedSecurity.securityId === securityId
      );
    }
    return false;
  }

  hasSymbol(enteredSymbol: Pick<AutoSuggestSymbol, 'symbol' | 'description' | 'symbolType' | 'market'>): boolean {
    if (this.selectedSecurities !== undefined) {
      return this.selectedSecurities.some(
        (selectedSecurity: SecuritiesTransfersHolding): boolean =>
          this.symbolComparator(enteredSymbol, selectedSecurity)
      );
    }
    return false;
  }

  isCashAmountEntered(): boolean {
    return this.cashAmount !== undefined && this.cashAmount > 0;
  }

  isContributionSuccess(): boolean {
    return this.confirmation === undefined ? false : this.confirmation.success;
  }

  private getMaxPositionsPerTransfer(): number {
    return this.transferClassification !== undefined && this.transferClassification.positionsLimitPerTransfer !== undefined
      ? this.transferClassification.positionsLimitPerTransfer : 35;
  }

  private isUsdSecurity(security: Pick<SecuritiesTransfersHolding, 'currency'>): boolean {
    return security !== undefined && security.currency === Currency.USD;
  }

  private symbolComparator(enteredSymbol: Pick<AutoSuggestSymbol, 'symbol' | 'description' | 'symbolType' | 'market'>,
                           selectedSecurity: SecuritiesTransfersHolding): boolean {
    let symbolMatch = true;
    let marketMatch = true;
    let descriptionMatch = true;
    let typeMatch;
    if (selectedSecurity.symbol !== undefined) {
      if (enteredSymbol.symbolType === SymbolType.FIXED_INCOME) {
        // Fixed income do not have symbols, so compare description instead
        descriptionMatch = selectedSecurity.name.toLowerCase() === enteredSymbol.description.toLowerCase();
      } else {
        symbolMatch = selectedSecurity.symbol.tickerSymbol.toLowerCase() === enteredSymbol.symbol.toLowerCase();
      }
      if (enteredSymbol.symbolType !== SymbolType.MUTUALFUND && enteredSymbol.symbolType !== SymbolType.FIXED_INCOME) {
        // Backend cannot access marketType for mutual funds or fixed income
        marketMatch = selectedSecurity.symbol.market.toLowerCase() === enteredSymbol.market.toLowerCase();
      }
      typeMatch = selectedSecurity.symbol.type.toLowerCase() === enteredSymbol.symbolType.toLowerCase();
      return symbolMatch && descriptionMatch && marketMatch && typeMatch;
    }
    return false;
  }
}

